<template>
    <el-container v-loading="loading"
                  class="h-full"
                  direction="vertical">
        <page-header v-if="approvalDocument">
            <template #title>
                {{ pageTitle }}
                <span v-if="approvalInfo?.is_izm"
                      class="_ml-0.5 font-bold">
                    Изм.
                </span>
            </template>

            <!-- Go back -->
            <template #button>
                <el-button @click="goBack">
                    <i class="el-icon-back" /> к списку
                </el-button>
            </template>

            <template v-if="canEditDocument">
                <el-button
                    class="_mr-1"
                    type="primary"
                    size="mini"
                    @click="onOpenEditDocument">
                    Редактировать документ
                </el-button>
            </template>

            <task-context 
                :task="task"
                @task-remove="handleDelete" />
        </page-header>

        <div v-if="pdfUrl"
             class="approval-pdf">
            <div id="canvas-container"
                 class="approval-pdf__wrapper" />

            <span
                class="el-image-viewer__btn el-image-viewer__close"
                @click="pdfUrl = null"><i class="el-icon-close" /></span>
        </div>
        <el-main class="pr-0 py-0">
            <el-row type="flex"
                    style="min-height: 100%">
                <el-col :span="18"
                        class="pr-4">
                    <el-tabs v-model="activeTabName"
                             @tab-click="handleClickTab">
                        <!-- Common -->
                        <el-tab-pane label="Информация"
                                     name="project.approval.info">
                            <el-row class="w-50 _mt-1 approval-iteration">
                                <el-row
                                    v-if="allDocuments"
                                    type="flex"
                                    class="row-gap-1.5 gap-0.75 flex-wrap--wrap"
                                    justify="left"
                                    align="center">
                                    <approval-card-item
                                        v-for="(doc, idx) in getIterationDocs(currentIteration)"
                                        :key="doc.id + 'approval.info' + idx"
                                        :document="doc"
                                        :stamp-promise="lastStampPromise" />
                                </el-row>

                                <el-row
                                    v-if="
                                        currentIteration.approvers && !isAddApproverScreen &&
                                            ((
                                                (approvalInfo.process_status || isCreator || isApprover)
                                                && !isGip)
                                                || (isGip && approvalInfo.process_status !== 'not_initialized')
                                            )
                                    "
                                    class="approval-route">
                                    <div v-if="currentIteration.approvers"
                                         class="mb-3">
                                        Маршрут согласования
                                    </div>

                                    <approver
                                        v-for="(approver, idx) in currentIteration.approvers"
                                        :key="approver.id + 'approval.info' + idx"
                                        :approver="approver"
                                        :index="idx" />
                                </el-row>
                            </el-row>

                            <el-button
                                v-if="
                                    approvalInfo.process_status === 'not_initialized' && isCreator
                                "
                                type="danger"
                                class="w-25"
                                size="mini"
                                @click="reject('stopped')">
                                Остановить процесс согласования
                            </el-button>

                            <el-form
                                ref="reportForm"
                                class="approval-form w-40 _mt-1 space-y-1"
                                label-width="12rem"
                                label-position="left"
                                size="mini"
                                :model="form"
                                :rules="rules"
                                @submit.native.prevent>
                                <!-- Process Date  -->
                                <el-row v-if="isGip">
                                    <date-field v-model="form.approvalEnd"
                                                class="reset-el-form-item"
                                                :disabled="!isEditableField('approval_end')"
                                                :allowed-from="approvalEndAllowedFrom"
                                                name="approvalEnd"
                                                label="Срок согласования"
                                                size="mini" />
                                </el-row>

                                <!-- List approvers  -->
                                <el-row v-if="((isApprover || isGip) && isTaskNotInitialized) || isAddApproverScreen"
                                        class="space-y-1">
                                    <user-field 
                                        v-for="(approver, i) in visibleApprovers" 
                                        :key="approver.id"
                                        :label="i === 0 ? 'Проектировщик': (processInitiator && approver?.user?.id === processInitiator?.id) ? 'ГИП' : `Согласующий ${approver?.user?.deleted ? '(Удален)' : ''}`"
                                        class="reset-el-form-item"
                                        name="approvers"
                                        :value="approver.user"
                                        :disabled="!isEditableField('approvers') || isDisabledApprover(approver)"
                                        :is-document-approver="isEditableField('approvers') && !isDisabledApprover(approver)"
                                        @change="id => changeApproverUser(approver, id)">
                                        <template v-if="i >= 1"
                                                  #extra-fields>
                                            <user-field-auto-approve-extra
                                                v-if="((processInitiator && approver?.user?.id !== processInitiator?.id) || !processInitiator) && !approver?.user?.deleted"
                                                class="_ml-1"
                                                :checkbox-value="approver.checked"
                                                :date-value="approver.date"
                                                @extra-fields-change="({ checkbox, date }) => updateApproverExtraFields(approver, checkbox, date)" />
        
                                            <el-button 
                                                v-if="isGip && !approver?.user?.deleted"
                                                size="mini"
                                                class="border--none _ml-1"
                                                type="text"
                                                @click="removeApprover(approver.id)">
                                                Удалить
                                            </el-button>
                                        </template>
                                    </user-field>
                                </el-row>

                                <!-- Add To List approvers  -->
                                <el-row v-if="(isGip && isTaskNotInitialized) || isAddApproverScreen">
                                    <el-form-item>
                                        <el-button
                                            class="w-full"
                                            type="primary"
                                            size="mini"
                                            icon="el-icon-plus"
                                            plain
                                            @click="addApproverToForm">
                                            Добавить
                                        </el-button>
                                    </el-form-item>
                                </el-row>

                                <el-divider class="m-0" />

                                <!-- Add To List approvers  -->
                                <el-row v-if="(isGip && isTaskNotInitialized) || isAddApproverScreen || (!isTaskNotInitialized && isAddApproverScreen)">
                                    <el-button
                                        v-if="isGip && isTaskNotInitialized"
                                        type="primary"
                                        size="mini"
                                        @click="initProcess">
                                        {{ isGip && !hasCreatedApprovers ? 'В производство работ' : 'Создать процесс' }}
                                    </el-button>

                                    <el-button
                                        v-if="isAddApproverScreen"
                                        type="info"
                                        size="mini"
                                        @click="isAddApproverScreen = false">
                                        Назад
                                    </el-button>

                                    <el-button
                                        v-if="!isTaskNotInitialized && isAddApproverScreen"
                                        type="primary"
                                        size="mini"
                                        @click="addApproverToProcess">
                                        Применить
                                    </el-button>
                                </el-row>

                                <!-- Comment and file  -->
                                <el-row v-if="isCanApprov && !isAddApproverScreen">
                                    <el-input
                                        v-model="form.comment"
                                        type="textarea"
                                        :autosize="{ minRows: 2 }"
                                        maxlength="200"
                                        show-word-limit
                                        placeholder="Введите комментарий..."
                                        autocomplete="off" />

                                    <el-upload v-if="!commentFile"
                                               v-model="commentFile"
                                               action=""
                                               :accept="'.xlsx, .xls, .doc, .docx, .pdf'"
                                               type="file"
                                               :show-file-list="false"
                                               :auto-upload="false"
                                               :multiple="false"
                                               :on-change="handleUploadCommentFile"
                                               :limit="1">
                                        <div class="_mt-1"
                                             style="display: flex; align-items: center; width: 100%; justify-content: flex-start;">
                                            <el-tooltip v-if="!form.comment"
                                                        content="Введите комментарий, чтобы иметь возможность подгрузитть файл"
                                                        placement="right">
                                                <el-button
                                                    type="primary"
                                                    size="mini"
                                                    :disabled="!form.comment">
                                                    Выбрать файл
                                                </el-button>
                                            </el-tooltip>

                                            <el-button v-else-if="form.comment"
                                                       type="primary"
                                                       size="mini">
                                                Выбрать файл
                                            </el-button>
                                        </div>
                                    </el-upload>

                                    <div v-if="commentFile"
                                         class="wrapper _mt-1"
                                         @mouseover="hoveredFile = commentFile"
                                         @mouseleave="hoveredFile = null">
                                        <div style="display: flex; align-items: center; gap: 10px;">
                                            <div class="icon">
                                                <icon
                                                    :name="getIcon(commentFile)"
                                                    class="w-3" />
                                            </div>
                                            <small>{{ commentFile.name }}</small>
                                            <div class="icon">
                                                <el-button v-if="!hoveredFile"
                                                           icon="el-icon-circle-check"
                                                           class="icon_green"
                                                           type="text" />
                                                <el-button v-if="hoveredFile"
                                                           icon="el-icon-close"
                                                           type="text"
                                                           @click="deleteCommentFile" />
                                            </div>
                                        </div>
                                    </div>
                                </el-row>

                                <!-- Controll Main -->
                                <el-row v-if="!isAddApproverScreen && (canEditIzmDocument || isCanApprov || isButtonShowAddApprover)">
                                    <el-button
                                        v-if="canEditIzmDocument"
                                        type="primary"
                                        size="mini"
                                        @click="onOpenEditIzmDocument">
                                        Загрузить Изм.
                                    </el-button>

                                    <el-button
                                        v-if="isCanApprov"
                                        style="flex: 1"
                                        type="primary"
                                        size="mini"
                                        @click="approve">
                                        {{ isGip ? 'В производство работ' : 'Согласовать' }}
                                    </el-button>

                                    <el-button
                                        v-if="isCanApprov"
                                        style="flex: 1"
                                        type="danger"
                                        size="mini"
                                        @click="reject('canceled')">
                                        Отклонить
                                    </el-button>

                                    <el-button
                                        v-if="isButtonShowAddApprover"
                                        style="flex: 1"
                                        size="mini"
                                        @click="switchAddApproverScreen">
                                        {{ isGip ? 'Изменить маршрут' : 'Добавить согласующего' }}
                                    </el-button>
                                </el-row>

                                <el-row
                                    v-if="
                                        isApproverDelete
                                    ">
                                    <div class="text-size--body text-status-rejected">
                                        Вы удалены из процесса
                                    </div>
                                </el-row>

                                <el-row
                                    v-if="
                                        !isCreator && approvalInfo.process_status === 'stopped'
                                    ">
                                    <div class="text-size--body text-status-rejected">
                                        Процесс остановлен проектировщиком
                                    </div>
                                </el-row>
                            </el-form>

                            <el-divider v-if="hasEditableFields"
                                        class="w-40" />

                            <!-- Delegation -->
                            <el-form v-if="hasEditableFields"
                                     ref="taskTypeApprovalProcess"
                                     :model="editedItem"
                                     class="w-40"
                                     label-width="12rem"
                                     label-position="left"
                                     size="mini"
                                     @submit.native.prevent>
                                <user-field v-model="editedItem.new_creator_id"
                                            name="new_creator_id"
                                            label="Проектировщик"
                                            :disabled="!isEditableField('new_creator_id')"
                                            :errors="errors['new_creator_id']"
                                            is-project-designer
                                            as-key>
                                    <template #extra-fields>
                                        <!-- GE history -->
                                        <trashed-members v-if="trashedMembers.project_designers?.length"
                                                         :members="trashedMembers.project_designers"
                                                         placement="right-start" />
                                    </template>
                                </user-field>   
                                <user-field v-if="isEditableField('new_acceptor_id')"
                                            v-model="editedItem.new_acceptor_id"
                                            name="new_acceptor_id"
                                            label="ГИП"
                                            :disabled="!isEditableField('new_acceptor_id')"
                                            :errors="errors['new_acceptor_id']"
                                            is-chief-project-designer
                                            as-key>
                                    <template #extra-fields>
                                        <!-- GE history -->
                                        <trashed-members v-if="trashedMembers.chief_project_designers?.length"
                                                         :members="trashedMembers.chief_project_designers"
                                                         placement="right-start" />
                                    </template>
                                </user-field>   
                                
                                <!-- Buttons -->
                                <div class="cols-minmax-min space-x-1">
                                    <!-- Confirm -->
                                    <el-button type="primary"
                                               size="mini"
                                               @click="update">
                                        Сохранить
                                    </el-button>
                                </div>
                            </el-form>
                        </el-tab-pane>

                        <!-- Archive -->
                        <el-tab-pane label="Архив"
                                     name="project.approval.archive">
                            <el-row
                                v-for="(iteration, index) in iterations"
                                :key="iteration.id + 'approval.archive' + index"
                                class="approval-iteration">
                                <div
                                    style="color: #606266"
                                    class="_mb-0.5 text-size--supplementary">
                                    {{
                                        index === iterations.length - 1
                                            ? "Актуальная итерация:"
                                            : `Итерация ${index + 1}:`
                                    }}
                                </div>
                                <el-row
                                    v-if="allDocuments"
                                    type="flex"
                                    class="row-gap-1.5 gap-0.75 flex-wrap--wrap"
                                    justify="left"
                                    align="center">
                                    <approval-card-item
                                        v-for="doc in getIterationDocs(iteration)"
                                        :key="doc.id + 'approval.archive' + iteration.id"
                                        :document="doc" />
                                </el-row>

                                <el-row
                                    v-if="
                                        iteration.approvers &&
                                            !isAddApproverScreen &&
                                            (approvalInfo.process_status || isCreator)
                                    "
                                    class="approval-route">
                                    <div v-if="isCreator"
                                         class="mb-3">
                                        Маршрут согласования
                                    </div>
                                    <approver
                                        v-for="(approver, idx) in iteration.approvers"
                                        :key="approver.id + 'approval.archive' + idx"
                                        :approver="approver"
                                        :index="idx" />
                                </el-row>

                                <el-divider
                                    class="approval-iteration__divider _mb-1.25 _mt-1.75" />
                            </el-row>
                        </el-tab-pane>
                    </el-tabs>
                </el-col>

                <el-col :span="7"
                        class="d-flex flex--grow">
                    <el-card
                        :body-style="{ padding: '16px' }"
                        shadow="always"
                        class="w-full border--none">
                        <approve-description
                            :info="approvalInfo"
                            :task="task"
                            :version="currentIteration.document_version"
                            :get-pdf-data="getPdfData"
                            :gip-or-approver="gipOrApprover"
                            :doc="approvalDocument"
                            :project-name="projectName"
                            @changeLoading="changeLoading" />
                    </el-card>
                </el-col>

                <!-- Task actions -->
                <portal to="approval-actions">                    
                    <task-actions 
                        :task="task"
                        :actions="allowedActions"
                        @do-with-data="act" />
                </portal>
            </el-row>
        </el-main>
    </el-container>
</template>
<script>
import { getDocument } from 'pdfjs-dist/build/pdf'
import { startOfDay } from 'date-fns'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import { resourceable } from '@/store/connectors'

import { extensionIcon } from '@/utils/ui'
import { parse } from '@/utils/date'
import { then } from '@/utils/immutable'
import { fromError } from '@/utils/common'

import autoApproveValidationMixin from '@/mixins/autoApproveValidation.mixin'

import { getEditableFields, statuses } from '@/models/approval'
import { getChiefProjectDesigner, getLastStampPromise, getNumberWithPrefix, getTrashedMembers } from '@/models/tasks'

import dialogs from '@/values/dialogs'

import PageHeader from '@/components/layout/PageHeader.vue'
import ApprovalCardItem from '@/components/cards/ApprovalCard.vue'
import Approver from '@/components/approval/Approver.vue'
import ApproveDescription from '@/components/approval/ApproveDescription.vue'
import UserFieldAutoApproveExtra from '@/components/approval/UserFieldAutoApproveExtra.vue'
import DateField from '@/components/fields/DateField'
import UserField from '@/components/fields/UserField.vue'
import TrashedMembers from '@/components/popovers/TrashedMembers'
import TaskActions from '@/components/tasks/TaskActions'
import TaskContext from '@/components/tasks/TaskContext'

export default {
  name: 'Approval',
  components: {
    PageHeader,
    ApprovalCardItem,
    Approver,
    ApproveDescription,
    UserFieldAutoApproveExtra,
    DateField,
    UserField,
    TrashedMembers,
    TaskActions,
    TaskContext
  },
  mixins: [
    resourceable({ on: 'tasks', name: 'allowedActions' }),

    autoApproveValidationMixin
  ],
  data: () => ({
    activeTabName: 'project.approval.info',
    loading: false,
    pdfPages: null,
    pdfUrl: null,
    commentFile: null,
    hoveredFile: null,
    agreement: false,
    processType: 1,
    isAddApproverScreen: false,
    form: {
      comment: null,
      creatorId: null,
      approvalEnd: null,
      documentId: null,
      approvers: [],
      deleteApprovers: []
    },

    editedItem: {},
    initialItem: {},
    errors: {}
  }),
  computed: {
    ...mapGetters('project', ['projectName']),
    ...mapGetters('approval', [
      'pageTitle',
      'approvalDocument',
      'approvalInfo',
      'iterations',
      'allDocuments',
      'currentIteration',
      'currentApprover',
      'isGip',
      'isApprover',
      'isCreator',
      'getCurrentApprover'
    ]),
    ...mapGetters('account', ['hasAccess']),
    ...mapGetters('auth', ['profile']),
    ...mapGetters('users', ['usersForSuggest']),
    ...mapGetters('tasks', { _isEditableField: 'isEditableField' }),
    ...mapGetters('tasks', ['hasEditableFields']),
    ...mapState('tasks', { task: (state) => state.task }),

    rules() {
      return {
        approvalEnd: {
          required: true,
          message: 'Выберите дату'
        },
        approvers: {
          required: true,
          trigger: 'blur',
          validator: (_rule, _value, callback) => {
            if (this.isGip || this.hasCreatedApprovers) {
              const approversWithCheckedBox = this.form.approvers.filter(approver => approver.checked === true);
              const approversWithoutDate = approversWithCheckedBox.filter(approver => !approver.date);
              
              if (approversWithoutDate.length > 0) {
                callback(new Error('Для согласующих с включенным автогласованием необходимо указать дату'))
                return;
              }

              callback()
            } else {
              callback(new Error('Укажите пользователей согласования'))
            }
          
          }
        }
      }
    },
    hasCreatedApprovers() {
      return !!this.form.approvers.filter(x => x._created && x.user).length
    },
    canEditDocument() {
      return (this.isTaskStopped || this.isTaskCanceled) && this.isCreator;
    },
    canEditIzmDocument() {
      return (this.isTaskApproved) && this.isCreator;
    },
    gipOrApprover() {
      if (!this.isCreator) {
        return this.profile;
      }

      return null;
    },
    hasComment() {
      return !!this.form.comment && this.form.comment !== ''
    },
    processInitiator() {
      return this.approvalInfo?.initiator
    },
    processNumber() {
      return getNumberWithPrefix(this.approvalDocument?.approval_process)
    },
    isProcessParallel() {
      return this.approvalInfo?.process_type === 'Parallel';
    },
    isProcessTypeDisabled() {
      return this.approvalInfo?.process_type !== null;
    },
    isNoFirstIteration() {
      return this.iterations.length > 1
    },
    isApproverDelete() {
      return this.getCurrentApprover?.deleted
    },
    isTaskNotInitialized() {
      return this.approvalInfo?.process_status === statuses.NotInitialized
    },
    isTaskStopped() {
      return this.approvalInfo?.process_status === statuses.Stopped
    },
    isTaskOnReview() {
      return this.approvalInfo?.process_status === statuses.OnReview
    },
    isTaskCanceled() {
      return this.approvalInfo?.process_status === statuses.Canceled
    },
    isTaskApproved() {
      return this.approvalInfo?.process_status === statuses.Approved
    },
    isTaskOnCpeApproval() {
      return this.approvalInfo?.process_status === statuses.OnCpeApproval
    },
    isCurrentApprover() {
      return this.currentApprover;
    },
    isBtnsDisabled() {
      return (!this.isTaskOnReview || !this.isCurrentApprover);
    },
    isButtonShowAddApprover() {
      if(this.isTaskNotInitialized || this.isTaskStopped || this.isTaskCanceled || this.isTaskApproved) {
        return false
      }

      return this.isCurrentApprover;
    },
    isCanApprov() {
      return (
        (
          this.isGip &&
          !this.isTaskCanceled &&
          !this.isTaskStopped &&
          (this.isTaskOnCpeApproval || !this.isBtnsDisabled)
        )
        ||
        (
          this.isApprover &&
          !this.isTaskCanceled &&
          !this.isTaskStopped &&
          !this.isBtnsDisabled
        )
      )
    },
    errorMessage() {
      return !this.isGip && this.isCanApprov ? 'Невозможно согласовать или отклонить документ' : 'Отображаемая информации неактуальна';
    },
    visibleApprovers() {
      const gipId = this.processInitiator?.id;
      const approvers = this.form.approvers || [];

      let gipSeen = false;

      return approvers.filter(approver => {
        if (!approver?.user || approver.user.id !== gipId) {
          return true; 
        }

        if (!gipSeen) {
          gipSeen = true;
          return true;
        }

        return false; 
      })
    },
    approvalEndAllowedFrom() {
      return startOfDay(new Date())
    },
    trashedMembers() {
      return getTrashedMembers(this.task) || {}
    },

    lastStampPromise() {
      return getLastStampPromise(this.task)
    },
  },
  watch: {
    approvalInfo(x) {
      x && (this.form.approvalEnd = parse(this.approvalInfo.approval_end, { iso: true }));
      x && (this.form.approvers = (this.currentIteration?.approvers || []).map(user => ({
        id: key(),
        user,
        ...(user.auto_approve_date ? { checked: true, date: user.auto_approve_date } : {})
      })));
      x && (this.processType = (this.isProcessParallel || this.approvalInfo?.process_type === null) ? 1 : 2);
    },

    task(x) {
      if (x) {
        this.editedItem = {
          new_creator_id: x.creator?.id,
          new_acceptor_id: getChiefProjectDesigner(x)?.id
        }
        this.initialItem = { ...this.editedItem }
      }
    }
  },
  created() {
    this.activeTabName = this.$route.name;
    this.init();
    this.fetchUsersForSuggest();
  },
  methods: {
    ...mapMutations({
      showForm: 'form/SHOW_FORM'
    }),
    ...mapMutations('dialog', { showDialog: 'SHOW_DIALOG' }),
    ...mapActions({
      confirm: 'dialogs/confirmation/confirm'
    }),
    ...mapActions('project', ['createProjectDocument']),
    ...mapActions('approval', [
      'updateProcessByApprover',
      'initApprovalProcess',
      'saveComment',
      'fetchApproval',
      'updateCommentFile',
      'deleteApprovalProcess'
    ]),
    ...mapActions('users', ['fetchUsersForSuggest']),
    ...mapActions('tasks', [
      'showTask',
      'updateTask',
      'makeTaskAction'
    ]),

    getIcon(file) {
      return extensionIcon(file.name)
    },
    async fetch() {
      await this.getTask()
      return this.fetchApproval({ processId: this.$route.params.processId });
    },
    goBack() {
      this.$router.push({
        name: 'project.tasks.approval'
      });
    },
    isEditableField(name) {
      return !!getEditableFields({status: this.approvalInfo.process_status})[name] || this._isEditableField(name)
    },
    isDisabledApprover(approver) {
      return !approver._created
    },
    handleClickTab() {
      this.$router.push({
        name: this.activeTabName
      });
    },
    changeLoading(value) {
      this.loading = value;
    },
    async onOpenEditDocument() {
      if (this.canEditDocument) {
        await this.showForm({
          formName: 'task-bar-form',
          formTitle: 'Редактировать документ',
          action: this.createProjectDocument,
          payload: this.approvalDocument,
          callback: () => this.fetch()
        });
      }
    },
    async onOpenEditIzmDocument() {
      if (this.canEditIzmDocument) {
        await this.showForm({
          formName: 'task-bar-form',
          formTitle: 'Загрузить изменения',
          formType: 'izm',
          action: this.createProjectDocument,
          payload: this.approvalDocument,
          callback: () => this.fetch()
        });
      }
    },
    handleDelete() {
      let callback = async () => {
        await this.deleteApprovalProcess({
          approverId: this.profile.id,
          processId: this.$route.params.processId
        })
          .then(() => {
            dialogs.deleted.bind(this, { message: 'Процесс согласования успешно удалён' })
            this.$router.push({
              name: 'project.tasks.approval'
            });
          })
          .catch(dialogs.notDeleted.bind(this, { message: 'При удалении произошла ошибка' }))
      };
      dialogs.confirmDeletion.call(this, { subject: 'процесс согласования' }).then(callback)
    },
    changeApproverUser({ id }, user) {
      const { approvers } = this.form || {}

      this.form = {
        ...this.form,
        approvers: approvers.map(x => x.id === id ? { ...x, user } : x)
      }
    },
    addApproverToForm() {
      this.form = {
        ...this.form,
        approvers: [...this.form.approvers, { id: key(), _created: true }]
      } 
    },
    removeApprover(id) {
      const { approvers } = this.form || {}

      this.form = {
        ...this.form,
        deleteApprovers: [...this.form.deleteApprovers, approvers.find(x => x.id === id)],
        approvers: approvers.filter(x => x.id !== id)
      }
    },
    switchAddApproverScreen() {
      this.form.approvalEnd = parse(this.approvalInfo.approval_end, { iso: true });

      this.isAddApproverScreen = !this.isAddApproverScreen;
    },
    getIterationDocs(iteration) {
      const documents = [];

      // add main doc
      documents.push({
        id: iteration?.document_id,
        version: iteration?.document_version,
        original_file_name: iteration?.document_name,
        newName: iteration?.document_name,
        isMainDoc: true
      })

      // add acc docs
      if(iteration.accompanying_documents.length){
        iteration.accompanying_documents.forEach((doc) => {
          documents.push({
            id: doc.id,
            newName: doc.original_file_name ?? '',
            version: doc.version
              ? doc.version
              : iteration?.document_version ?? '',
            original_file_name: doc.original_file_name,
            isMainDoc: false
          })
        })
      }

      return documents.filter(Boolean).sort((a, b) => b.version - a.version);
    },
    async init() {
      try {
        this.loading = true;

        await this.fetch();

        if (this.canEditDocument) {
          await this.showForm({
            formName: 'task-bar-form',
            formTitle: 'Редактировать документ',
            action: this.createProjectDocument,
            payload: this.approvalDocument,
            callback: () => this.fetch()
          });
        }
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'При загрузке произошла ошибка'
        });
      } finally {
        this.loading = false;
      }
    },
    async getPdfData(url) {
      this.pdfUrl = url;

      const cMapUrl = '../../../node_modules/pdfjs-dist/cmaps';
      const cMapPacked = true;
      const enableXfa = true;
      const loadingTask = await getDocument({
        url: this.pdfUrl,
        cMapUrl,
        cMapPacked,
        enableXfa
      });
      const canvasArray = [];

      loadingTask.promise.then(
        function (pdf) {
          let totalPages = pdf.numPages;

          function renderPage(pageNumber) {
            pdf.getPage(pageNumber).then(function (page) {
              const scale = 1;
              const viewport = page.getViewport({ scale: scale });

              const canvas = document.createElement('canvas');
              const context = canvas.getContext('2d');
              canvas.height = viewport.height;
              canvas.width = viewport.width;

              const renderContext = {
                canvasContext: context,
                viewport: viewport
              };

              const renderTask = page.render(renderContext);
              renderTask.promise.then(function () {
                canvasArray.push(canvas);

                if (pageNumber < totalPages) {
                  renderPage(pageNumber + 1);
                } else {
                  displayCanvases(canvasArray);
                }
              });
            });
          }

          function displayCanvases(canvases) {
            const container = document.getElementById('canvas-container');

            canvases.forEach((canvas) => {
              container.appendChild(canvas);
            });
          }

          renderPage(1);
        },
        function (reason) {
          console.error(reason);
        }
      );
    },
    async addApproverToProcess() {
      if (!this.validateAutoApprove()) {
        return;
      }

      try {
        this.loading = true;

        const delete_author_ids = this.form.deleteApprovers.map(({ user }) => user?.id).filter(is);
        const newApprovers = this.form.approvers.filter(a => a._created && a.user && a.user.id);
        const editedApprovers = this.form.approvers.filter(a => !a._created && a._edited && a.user && a.user.id);

        await this.updateProcessByApprover({
          processId: this.approvalInfo.id,
          payload: {
            approver_id: this.profile.id,
            approval_status: 'change_another_authors',
            updated_at: this.approvalInfo.updated_at,
            delete_author_ids,
            need_approve_from_authors: newApprovers.map(({ user, checked, date }) => ({
              id: user.id,
              ...(checked && date && { autoapprovedate: date })
            })),
            changed_approve_authors: editedApprovers.map(({ user, checked, date }) => ({
              id: user.id,
              ...(checked && date && { autoapprovedate: date })
            }))
          }
        });

        await this.fetch();

        this.$notify({
          type: 'success',
          title: 'Согласующие изменены'
        });
      } catch (e) {
        console.error(e)
        dialogs.failed.call(this, { message: this.errorMessage })
      } finally {
        this.loading = false;
        this.isAddApproverScreen = false;
        this.form.deleteApprovers = [];
        this.form.approvers = this.form.approvers.map(approvers => {
          return { ...approvers, _created: false, _edited: false };
        })
      }
    },

    handleUploadCommentFile(file) {
      this.commentFile = file.raw;
    },

    deleteCommentFile() {
      this.commentFile = null;
    },

    async approve() {
      try {
        this.loading = true;

        let fileId = ''
        if (this.commentFile) {
          const result = await this.updateCommentFile(this.commentFile);
          fileId = result.data
        }

        if(this.hasComment) {
          await this.saveComment({
            text: this.form.comment,
            approver_id: this.profile.id,
            iteration_id: this.currentIteration?.id,
            file_id: this.commentFile ? fileId : null
          })
          this.form.comment = '';
        }

        await this.updateProcessByApprover({
          processId: this.approvalInfo.id,
          payload : {
            approver_id: this.profile.id,
            approval_status: 'approved',
            updated_at: this.approvalInfo.updated_at
          }
        });

        await this.fetch();
      } catch (e) {
        console.error(e)
        dialogs.failed.call(this, { message: this.errorMessage })
      } finally {
        this.loading = false;
      }
    },
    async reject(status) {
      if (status === 'canceled' && !this.form.comment) {
        this.$notify({
          type: 'warning',
          title: 'Необходимо написать комментарий'
        });
        return;
      }
      this.loading = true;

      try {
        if(this.hasComment && status !== 'stopped') {
          let fileId = ''
          if (this.commentFile) {
            const result = await this.updateCommentFile(this.commentFile);
            fileId = result.data
          }
          await this.saveComment({
            text: this.form.comment,
            approver_id: this.profile.id,
            iteration_id: this.currentIteration?.id,
            file_id: this.commentFile ? fileId : null
          })
          this.form.comment = '';
        }
        await this.updateProcessByApprover({
          processId: this.approvalInfo.id,
          payload: {
            approver_id: this.profile.id,
            approval_status: status,
            updated_at: this.approvalInfo.updated_at
          }
        });

        await this.fetch();

        if (!this.isCreator) return;

        this.$message({
          type: 'success',
          message:
            status === 'stopped'
              ? 'Процесс остановлен проектировщиком'
              : 'Согласование отклонено'
        });
      } catch (e) {
        console.error(e)
        dialogs.failed.call(this, { message: this.errorMessage })
      } finally {
        this.loading = false;
      }
    },
    async initProcess() {
      this.$refs.reportForm.validate(async (valid) => {
        if (valid) {
          if (!this.validateAutoApprove()) {
            return;
          }
          try {
            this.loading = true;

            await this.initApprovalProcess({
              processId: this.approvalInfo.id,
              payload: {
                initiator_id: this.profile.id,
                process_name: this.approvalInfo.process_name,
                process_type: this.processType,
                approval_start: new Date().toISOString(),
                approval_end: this.form.approvalEnd,
                approvers: this.form.approvers
                  .filter(({ _created, user }) => _created && user?.id)
                  .map(({ user, checked, date }) => ({
                    id: user.id,
                    ...(checked && date && { autoapprovedate: date })
                  }))
              }
            });

            await this.fetch();
          } catch (e) {
            console.error(e)
            dialogs.failed.call(this, { message: this.errorMessage })
          } finally {
            this.loading = false;
            this.agreement = false;
          }
        }
      });
    },
    updateApproverExtraFields(approver, checkbox, date) {
      if (!approver._created) {
        approver._edited = true;
      } else {
        delete approver._edited;
      }

      approver.checked = checkbox;
      approver.date = date;
    },

    getTask() {
      return this.showTask({
        taskId: this.$route.params.processId,
        payload: {
          params: {
            include: [
              'project',
              'creator',
              'members.user.organization',
              'membersTrashed.user.organization',
              'stampPromises'
            ]
          }
        }
      }).catch(console.error)
    },

    update() {
      this.loading = true

      return this.updateTask({
        taskId: this.$route.params.processId,
        payload: {
          ...this.prepareEditedItem()
        }
      })
        .catch(({ messages }) => then(messages, x => this.errors = { ...x }))
        .finally(() => this.loading = false)
    },

    prepareEditedItem(replaceableFields = {}) {
      return Object.entries(this.editedItem).reduce((r, [k, v]) => {
        const replacer = replaceableFields[k] || {}
        const comparator = replacer.comparator || ((a, b) => a === b)
        
        if (!comparator(v, this.initialItem[k])) {
          if (replacer.unwrap && replacer.to)
            return { ...r, ...replacer.to(v)} 

          return {
            ...r,
            [replaceableFields[k]?.key || k]: replaceableFields[k]?.to ? replaceableFields[k]?.to(v) : v
          }
        }
        
        return r
      }, {})
    },

    act(action) {
      const { name, translated_name, allowed_fields } = action

      const go = (data = {}) => {
        this.loading = true

        return this.makeTaskAction({
          taskId: this.task.id,
          taskType: this.task.type,
          payload: {
            action: name,
            data
          }
        })
          .catch(fromError)
          .catch(({ messages }) => {
            throw { messages }
          })
          .finally(() => this.loading = false)
      }

      (allowed_fields.length 
        ? () => this.showDialog({
          dialogName: 'task-approval-process-dialog',
          payload: { action, task: this.task, approval: this.approvalInfo, approvalDocument: this.approvalDocument },
          action: go
        })
        : () => this.confirm({
          title: `Вы уверены, что хотите ${translated_name?.toLowerCase() || 'отменить последнее решение'}?`,
          message: 'После отмены решения текст замечания и прикрепленный к нему документ будут удалены',
          callback: go
        }))()
    }
  }
};
</script>
<style lang="scss">
.approval-route {
  margin-top: 16px;
  font-size: 12px;
  color: #606266;

  & > div:last-of-type .line-route {
    width: 0;
  }
}

.approval-form {
  .el-form-item {
    & .el-form-item__content {
      display: flex;
      align-items: center;
      
      .el-select {
        max-width: 28rem;
        min-width: 28rem;
      
        @media screen and (max-width: 1900px) {
          max-width: 12rem;
          min-width: 12rem;
        }
      }

      & .el-form-item__error {
        font-size: 10px;
      }
    }
  }
}

.approval-iteration {
  &:last-of-type {
    margin-bottom: 16px;

    & .approval-iteration__divider {
      display: none;
    }
  }
}

.el-radio-button__orig-radio:disabled + .el-radio-button__inner {
  color: #b6bac2;
  border-color: #E4E7ED;
}

.approval-agreement {
  font-size: 11px;
  font-weight: 400;
  color: #606266;
}

.approval-pdf {
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
  width: 100%;
  height: 100%;
  z-index: 1999;
  background: rgba(0, 0, 0, 0.5);

  &__wrapper {
    display: flex;
    background: #f4f4f5;
    border-radius: 12px;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    position: relative;
    left: 50%;
    top: 5%;
    transform: translate(-50%);
    height: 85%;
    max-width: 920px;
    overflow-y: scroll;
  }

  &__page {
    width: 550px;

    &_horizontal {
      width: 800px;
    }
  }

  &__toolbar {
    position: fixed;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.8;
    cursor: pointer;
    box-sizing: border-box;
    user-select: none;
    left: 50%;
    bottom: 30px;
    transform: translateX(-50%);
    width: 282px;
    height: 44px;
    padding: 0 23px;
    background-color: #606266;
    border-color: #fff;
    border-radius: 22px;
  }
}

.el-form-item.is-required:not(.is-no-asterisk) > .el-form-item__label:before {
  position: relative;
  left: 0;
}

.wrapper {
  width: fit-content;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  transition: background .3s;

  &:hover {
    background: #F5F7FA;
    transition: background .3s;
  }
}

.icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  color: #000;

  & .el-button {
    color: inherit;
  }

  &_green {
    color: #67C23A !important;
  }
}

.text-status-rejected {
  width: fit-content;
  padding: 0.1rem 0.75rem;
  border-radius: 4px;
  background: rgba(245, 108, 108, 0.1);
  color: #f56c6c;
  font-weight: 500;
}
</style>
